import { Typography } from '@remarkable/ark-web';
import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useCart, useSetCountry } from 'src/api/queries';
import amex from 'src/assets/icons/amex.svg';
import mastercard from 'src/assets/icons/mastercard.svg';
import paypal from 'src/assets/icons/paypal.svg';
import visa from 'src/assets/icons/visa.svg';
import {
  Button,
  Divider,
  NotificationBox,
  SomethingWentWrong,
  Spinner,
} from 'src/components';
import { useCheckoutForm } from '../checkoutFormStore';
import { CartDetails } from './CartDetails';
import { CartDetailsOffer } from './CartDetailsOffer';
import { CheckoutDetails } from './CheckoutDetails';
import { CheckoutDetailsForm } from './CheckoutDetailsForm';
import { CheckoutPaymentForm } from './CheckoutPaymentForm';

import { Trans, useLingui } from '@lingui/react/macro';
import { COUNTRIES } from 'src/utils/countryList';
import { createContentSwitch } from 'src/utils/createContentSwitch';
import { useStripeClient } from 'src/utils/stripe';
import { useWindowSize } from 'src/utils/useWindowSize';

export const CheckoutForm = () => {
  const { t } = useLingui();
  const [checkoutFormState, setCheckoutFormState] = useCheckoutForm();
  const cart = useCart(true);

  const publishableKey = cart.setup.data?.publishableKey;
  const clientSecret = cart.setup.data?.clientSecret;

  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const [redirectError, setRedirectError] = useState(false);

  const stripe = useStripeClient(publishableKey);
  const paymentRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();
  const paymentStep =
    checkoutFormState.currentStep !== 'DETAILS' &&
    checkoutFormState.country &&
    checkoutFormState.firstName &&
    checkoutFormState.lastName;
  const showOnCheckoutType = createContentSwitch(cart.checkoutType);
  const setCountry = useSetCountry();
  useEffect(() => {
    if (search.redirect_status === 'failed') {
      setRedirectError(true);
      void navigate({ to: '.', replace: true, search: undefined });
    }
  }, []);

  // Sync country selection from store
  useEffect(() => {
    if (checkoutFormState.country) {
      const newCountry = COUNTRIES.find(
        (c) => c.value == checkoutFormState.country
      );
      if (newCountry) {
        void setCountry(newCountry);
        // force details step to trigger tax calculation before payment
        checkoutFormState.currentStep = 'DETAILS';
      }
    }
  }, []);

  // scroll to payment form on mobile
  useEffect(() => {
    if (
      width < 768 &&
      checkoutFormState.currentStep === 'PAYMENT' &&
      stripe.isSuccess &&
      paymentRef.current
    ) {
      const elementPosition =
        paymentRef.current.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - 64; // Approximate height of header

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }, [checkoutFormState.currentStep, stripe.isSuccess]);

  const options: StripeElementsOptions | undefined = useMemo(() => {
    if (!clientSecret) {
      return undefined;
    }
    return {
      clientSecret,
    };
  }, [clientSecret]);

  if (cart.setup.isError) {
    return <SomethingWentWrong error={cart.setup.error} />;
  }

  return (
    <>
      {redirectError && (
        <NotificationBox
          className="mx-auto my-24"
          variant="error"
          title={<Trans>Payment failed</Trans>}
          data-cy="redirect-error"
        >
          <Typography variant="body-md">
            <Trans>Please try again with a different payment method.</Trans>
          </Typography>
        </NotificationBox>
      )}

      <div className="flex flex-wrap-reverse gap-32 ll:gap-48">
        <div className="order-2 flex flex-[1_1_400px] flex-col gap-24">
          <Typography
            className="hidden ll:block"
            variant="responsive-heading-sm"
          >
            <Trans>Order summary</Trans>
          </Typography>
          {showOnCheckoutType({
            ['connectOffer']: <CartDetailsOffer cart={cart} />,
            ['winbackOffer']: <CartDetailsOffer cart={cart} />,
            ['dmOffer']: <CartDetailsOffer cart={cart} />,
            ['store']: <CartDetails cart={cart} />,
          })}
        </div>
        <div className="order-1 flex flex-[1_1_400px] flex-col gap-40">
          <div className="flex flex-col gap-24">
            <div className="flex justify-start gap-24">
              <Typography variant="responsive-heading-sm">
                <Trans>1. Contact information</Trans>
              </Typography>

              {paymentStep && (
                <Button
                  variant="tertiary-neutral"
                  size="small"
                  onClick={() => {
                    setCheckoutFormState({
                      ...checkoutFormState,
                      currentStep: 'DETAILS',
                    });
                  }}
                  analytics={{
                    text: t`Edit`,
                    location: ComponentLocations.CHECKOUT.EDIT,
                    action: 'edit contact details',
                  }}
                >
                  <Trans>Edit</Trans>
                </Button>
              )}
            </div>
            {paymentStep ? (
              <CheckoutDetails />
            ) : (
              <CheckoutDetailsForm cart={cart} />
            )}
          </div>
          <Divider />

          <div ref={paymentRef} className="flex flex-col gap-16">
            <div className="flex flex-row justify-between gap-6">
              <Typography textWrap="nowrap" variant="responsive-heading-sm">
                <Trans>2. Payment</Trans>
              </Typography>
              <div className="flex justify-start gap-8">
                <img width={42} src={visa} alt="Visa"></img>
                <img width={42} src={mastercard} alt="Mastercard"></img>
                <img width={42} src={amex} alt="American express"></img>
                <img width={42} src={paypal} alt="PayPal"></img>
              </div>
            </div>
            {checkoutFormState.currentStep === 'PAYMENT' && (
              <>
                {stripe.isFetching && <Spinner />}
                {stripe.isError && <NotificationBox error={stripe.error} />}
                {stripe.isSuccess && options && (
                  <Elements stripe={stripe.data} options={options}>
                    <CheckoutPaymentForm cart={cart} />
                  </Elements>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
